import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import './SFResearch.css';
import { parseCSV } from '../utils/csvParser';
import * as XLSX from 'xlsx';

const SFResearch = () => {
  const [researchData, setResearchData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [methodologyFilter, setMethodologyFilter] = useState('');
  const [researchDesignFilter, setResearchDesignFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [serverStatus, setServerStatus] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    author: '',
    title: '',
    journal: '',
    year: '',
    url: '',
    studytype: '',
    methodology: '',
    researchdesign: ''
  });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginData, setLoginData] = useState({
    password: ''
  });
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [hoveredItem, setHoveredItem] = useState(null);

  useEffect(() => {
    // Check server status first
    fetch(`${API_URL}/api/status`)
      .then(res => res.json())
      .then(status => {
        setServerStatus(status);
        console.log('Server status:', status);
      })
      .catch(error => {
        console.log('Backend server not available');
        setServerStatus({ server: 'offline', mongodb: 'unavailable', mode: 'csv-only' });
      });

    // Load research data from backend API
    fetch(`${API_URL}/api/research`)
      .then(res => res.json())
      .then(data => {
        // Check if data is an array, if not use fallback
        if (Array.isArray(data)) {
          setResearchData(data);
          setFilteredData(data);
        } else {
          console.log('Backend returned non-array data, using CSV fallback');
          throw new Error('Invalid data format from backend');
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading SF research data:', error);
        // Fallback to CSV if backend is not available
        fetch('/content/sf-research.csv')
          .then(res => res.text())
          .then(csvText => {
            const data = parseCSV(csvText);
            setResearchData(data);
            setFilteredData(data);
            setLoading(false);
          })
          .catch(csvError => {
            console.error('Error loading CSV fallback:', csvError);
            setResearchData([]);
            setFilteredData([]);
            setLoading(false);
          });
      });
  }, []);

  useEffect(() => {
    // Filter data based on search term and filters
    if (!Array.isArray(researchData)) {
      return;
    }

    let filtered = researchData;

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.author?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.journal?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.studytype?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.methodology?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.researchdesign?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (yearFilter) {
      filtered = filtered.filter(item => item.year === yearFilter);
    }

    if (typeFilter) {
      filtered = filtered.filter(item => item.studytype === typeFilter);
    }

    if (methodologyFilter) {
      filtered = filtered.filter(item => item.methodology === methodologyFilter);
    }

    if (researchDesignFilter) {
      filtered = filtered.filter(item => item.researchdesign === researchDesignFilter);
    }

    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, yearFilter, typeFilter, methodologyFilter, researchDesignFilter, researchData]);

  // Get unique years and types for filter options
  const uniqueYears = Array.isArray(researchData) ? [...new Set(researchData.map(item => item.year))].sort((a, b) => b - a) : [];
  const uniqueTypes = Array.isArray(researchData) ? [...new Set(researchData.map(item => item.studytype))].sort() : [];
  const uniqueMethodologies = Array.isArray(researchData) ? [...new Set(researchData.map(item => item.methodology).filter(m => m))].sort() : [];
  const uniqueDesigns = Array.isArray(researchData) ? [...new Set(researchData.map(item => item.researchdesign).filter(d => d))].sort() : [];

  const clearFilters = () => {
    setSearchTerm('');
    setYearFilter('');
    setTypeFilter('');
    setMethodologyFilter('');
    setResearchDesignFilter('');
    setCurrentPage(1);
  };

  // Calculate statistics dynamically from research data
  const calculateStats = () => {
    if (!Array.isArray(researchData)) return {};

    const totalPapers = researchData.length;

    // Get min and max year from data
    const years = researchData
      .map(item => parseInt(item.year))
      .filter(year => !isNaN(year))
      .sort((a, b) => a - b);
    const minYear = years.length > 0 ? years[0] : null;
    const maxYear = years.length > 0 ? years[years.length - 1] : null;

    // Empirical Studies = Outcome + Process-outcome + Process + Correlational + Measurement validation
    const outcome = researchData.filter(item => {
      const study = item.studytype?.toLowerCase() || '';
      return study === 'outcome' && !study.includes('process');
    }).length;
    const processOutcome = researchData.filter(item => {
      const study = item.studytype?.toLowerCase() || '';
      return study.includes('process') && study.includes('outcome');
    }).length;
    const process = researchData.filter(item => {
      const study = item.studytype?.toLowerCase() || '';
      return study === 'process' && !study.includes('outcome');
    }).length;
    const correlational = researchData.filter(item =>
      item.studytype?.toLowerCase() === 'correlational'
    ).length;
    const measurementValidation = researchData.filter(item =>
      item.studytype?.toLowerCase() === 'measurement validation'
    ).length;

    const empiricalStudies = outcome + processOutcome + process + correlational + measurementValidation;

    // Reviews
    const reviews = researchData.filter(item =>
      item.studytype?.toLowerCase() === 'review'
    ).length;

    const theoretical = researchData.filter(item =>
      item.studytype?.toLowerCase() === 'theoretical'
    ).length;

    // Methodology breakdown - count studies by methodology (within empirical studies only)
    const empiricalStudyTypes = ['outcome', 'process', 'process-outcome', 'process outcome', 'correlational', 'measurement validation'];

    const quantitative = researchData.filter(item => {
      const study = item.studytype?.toLowerCase() || '';
      const isEmpirical = empiricalStudyTypes.some(type => study.includes(type));
      return isEmpirical && item.methodology === 'Quantitative';
    }).length;

    const qualitative = researchData.filter(item => {
      const study = item.studytype?.toLowerCase() || '';
      const isEmpirical = empiricalStudyTypes.some(type => study.includes(type));
      return isEmpirical && item.methodology === 'Qualitative';
    }).length;

    const mixedMethods = researchData.filter(item => {
      const study = item.studytype?.toLowerCase() || '';
      const isEmpirical = empiricalStudyTypes.some(type => study.includes(type));
      return isEmpirical && item.methodology === 'Mixed methods';
    }).length;


    // Research designs - COUNT ALL regardless of methodology (like dropdown does)
    const rct = researchData.filter(item => item.researchdesign === 'RCT').length;
    const experiments = researchData.filter(item => item.researchdesign === 'Experiment').length;
    const quasiExperiments = researchData.filter(item => item.researchdesign === 'Quasi-experiment').length;
    const singleGroup = researchData.filter(item => item.researchdesign === 'Single-group').length;
    const caseStudyQuant = researchData.filter(item => item.researchdesign === 'Case-study').length;
    const crossSectional = researchData.filter(item => item.researchdesign === 'Cross-sectional').length;
    const longitudinal = researchData.filter(item => item.researchdesign === 'Longitudinal').length;
    const delphi = researchData.filter(item => item.researchdesign === 'Delphi study').length;

    // Total quantitative designs
    const quantitativeDesignTotal = rct + experiments + quasiExperiments + singleGroup + caseStudyQuant + crossSectional + longitudinal + delphi;

    // Qualitative research designs - COUNT ALL regardless of methodology (like dropdown does)
    const thematicAnalysis = researchData.filter(item => item.researchdesign === 'Thematic A.').length;
    const phenomenologicalAnalysis = researchData.filter(item => item.researchdesign === 'Phenomenological A.').length;
    const contentAnalysis = researchData.filter(item => item.researchdesign === 'Content A.').length;
    const discourseAnalysis = researchData.filter(item => item.researchdesign === 'Discourse A.').length;
    const groundedTheory = researchData.filter(item => item.researchdesign === 'Grounded theory').length;
    const caseAnalysis = researchData.filter(item => item.researchdesign === 'Case analysis').length;
    const actionResearch = researchData.filter(item => item.researchdesign === 'Action research').length;
    const genericQualitative = researchData.filter(item => item.researchdesign === 'Generic').length;
    const microanalysis = researchData.filter(item => item.researchdesign === 'Microanalysis').length;
    const narrativeAnalysis = researchData.filter(item => item.researchdesign === 'Narrative').length;

    // Total qualitative designs
    const qualitativeDesignTotal = thematicAnalysis + phenomenologicalAnalysis + contentAnalysis + discourseAnalysis + groundedTheory + caseAnalysis + actionResearch + genericQualitative + microanalysis + narrativeAnalysis;

    // Review breakdown
    const systematicReviews = researchData.filter(item =>
      item.studytype?.toLowerCase() === 'review' && item.methodology === 'Systematic'
    ).length;
    const metaAnalyses = researchData.filter(item =>
      item.studytype?.toLowerCase() === 'review' && item.methodology === 'Systematic' && item.researchdesign === 'Meta-analysis'
    ).length;
    const metaSyntheses = researchData.filter(item =>
      item.studytype?.toLowerCase() === 'review' && item.methodology === 'Systematic' && item.researchdesign === 'Meta-synthesis'
    ).length;
    const descriptiveReviews = researchData.filter(item =>
      item.studytype?.toLowerCase() === 'review' && item.methodology === 'Systematic' && item.researchdesign === 'Descriptive'
    ).length;
    const bibliometricReviews = researchData.filter(item =>
      item.studytype?.toLowerCase() === 'review' && item.methodology === 'Systematic' && item.researchdesign === 'Bibliometric'
    ).length;
    const scopingReviews = researchData.filter(item =>
      item.studytype?.toLowerCase() === 'review' && item.methodology === 'Scoping'
    ).length;
    const traditionalReviews = researchData.filter(item =>
      item.studytype?.toLowerCase() === 'review' && item.methodology === 'Traditional'
    ).length;
    const overviewReviews = researchData.filter(item =>
      item.studytype?.toLowerCase() === 'review' && item.methodology === 'Overview'
    ).length;

    // Theoretical breakdown
    const practiceDescription = researchData.filter(item =>
      item.studytype?.toLowerCase() === 'theoretical' && item.methodology === 'Practice description'
    ).length;
    const conceptualDiscussion = researchData.filter(item =>
      item.studytype?.toLowerCase() === 'theoretical' && item.methodology === 'Conceptual discussion'
    ).length;
    const interventionDevelopment = researchData.filter(item =>
      item.studytype?.toLowerCase() === 'theoretical' && item.methodology === 'Intervention development'
    ).length;

    return {
      totalPapers,
      minYear,
      maxYear,
      empiricalStudies,
      outcome,
      processOutcome,
      process,
      correlational,
      measurementValidation,
      reviews,
      theoretical,
      quantitative,
      qualitative,
      mixedMethods,
      // Quantitative designs
      rct,
      experiments,
      quasiExperiments,
      singleGroup,
      caseStudyQuant,
      crossSectional,
      longitudinal,
      delphi,
      quantitativeDesignTotal,
      // Qualitative designs
      thematicAnalysis,
      phenomenologicalAnalysis,
      contentAnalysis,
      discourseAnalysis,
      groundedTheory,
      caseAnalysis,
      actionResearch,
      genericQualitative,
      microanalysis,
      narrativeAnalysis,
      qualitativeDesignTotal,
      // Review breakdown
      systematicReviews,
      metaAnalyses,
      metaSyntheses,
      descriptiveReviews,
      bibliometricReviews,
      scopingReviews,
      traditionalReviews,
      overviewReviews,
      // Theoretical breakdown
      practiceDescription,
      conceptualDiscussion,
      interventionDevelopment
    };
  };

  const stats = calculateStats();

  // Calculate pagination
  const totalPages = Array.isArray(filteredData) ? Math.ceil(filteredData.length / itemsPerPage) : 0;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = Array.isArray(filteredData) ? filteredData.slice(startIndex, endIndex) : [];

  const goToPage = (page) => {
    setCurrentPage(page);
    // Scroll to the research grid section instead of top
    const researchGrid = document.querySelector('.research-grid');
    if (researchGrid) {
      researchGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getPaginationNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const getTooltipText = (item) => {
    // Bereid title en authors voor tooltip
    const parts = [];

    if (item.title) {
      parts.push(item.title);
    }

    if (item.author) {
      parts.push(`By: ${item.author}`);
    }

    return parts.join(' — ');
  };

  const handleMouseMove = (e, itemId) => {
    const offsetX = 15;
    const offsetY = 15;
    setTooltipPos({
      x: e.clientX + offsetX,
      y: e.clientY + offsetY
    });
    setHoveredItem(itemId);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const handleAddNew = () => {
    setFormData({
      author: '',
      title: '',
      journal: '',
      year: '',
      url: '',
      studytype: '',
      methodology: '',
      researchdesign: ''
    });
    setEditingItem(null);
    setShowAddForm(true);
  };

  const handleEdit = (item) => {
    setFormData({
      author: item.author || '',
      title: item.title || '',
      journal: item.journal || '',
      year: item.year || '',
      url: item.url || '',
      studytype: item.studytype || '',
      methodology: item.methodology || '',
      researchdesign: item.researchdesign || ''
    });
    setEditingItem(item);
    setShowAddForm(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.title}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/research/${item._id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        alert('Research paper deleted successfully!');
        // Reload data
        window.location.reload();
      } else {
        const error = await response.json();
        alert('Error deleting research paper: ' + error.error);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Error deleting research paper. Please try again.');
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.author) {
      alert('Please fill in at least the title and author fields.');
      return;
    }

    try {
      const url = editingItem
        ? `${API_URL}/api/research/${editingItem._id}`
        : `${API_URL}/api/research`;

      const method = editingItem ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert(editingItem ? 'Research paper updated successfully!' : 'Research paper added successfully!');
        setShowAddForm(false);
        // Reload data
        window.location.reload();
      } else {
        const error = await response.json();
        alert('Error saving research paper: ' + error.error);
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Error saving research paper. Please try again.');
    }
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: loginData.password }),
      });

      if (response.ok) {
        const data = await response.json();
        setIsLoggedIn(true);
        setShowLoginForm(false);
        setLoginData({ password: '' });
        alert('Successfully logged in as admin!');
      } else {
        alert('Invalid password. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Error logging in. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setShowAddForm(false);
    alert('Logged out successfully.');
  };

  const handleDownloadExcel = () => {
    if (!Array.isArray(researchData) || researchData.length === 0) {
      alert('No data available to download.');
      return;
    }

    try {
      // Prepare data for Excel export
      const exportData = researchData.map(item => ({
        Author: item.author || '',
        Title: item.title || '',
        Journal: item.journal || '',
        Year: item.year || '',
        URL: item.url || '',
        'Study Type': item.studytype || '',
        Methodology: item.methodology || '',
        'Research Design': item.researchdesign || ''
      }));

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Research Data');

      // Set column widths for better readability
      const columnWidths = [
        { wch: 25 }, // Author
        { wch: 40 }, // Title
        { wch: 20 }, // Journal
        { wch: 8 },  // Year
        { wch: 30 }, // URL
        { wch: 18 }, // Study Type
        { wch: 15 }, // Methodology
        { wch: 20 }  // Research Design
      ];
      worksheet['!cols'] = columnWidths;

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `SFA-PubList-${timestamp}.xlsx`;

      // Write file
      XLSX.writeFile(workbook, filename);
      alert(`Successfully downloaded ${researchData.length} research papers to ${filename}`);
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      alert('Error downloading Excel file. Please try again.');
    }
  };

  const handleLoginChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <div className="sf-research">
        <div className="container">
          <div className="loading">Loading SF Research collection...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="sf-research">
      {/* Hero Section */}
      <section
        className="sf-research-hero"
        style={{
          backgroundImage: `linear-gradient(rgba(44, 62, 80, 0.8), rgba(52, 152, 219, 0.8)), url(${process.env.PUBLIC_URL}/images/sfpublist.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="container">
          <h1 className="page-title">SFA PubList by EBTA</h1>
          <p className="hero-subtitle">
            Solution-Focused Approach Publication List maintained by the European Brief Therapy Association Research Task Group
          </p>
        </div>
      </section>

      {/* Login Form Modal */}
      {showLoginForm && (
        <section className="form-modal">
          <div className="container">
            <div className="form-modal-content">
              <div className="form-header">
                <h3>🔐 Admin Login</h3>
                <button onClick={() => setShowLoginForm(false)} className="close-btn">✕</button>
              </div>

              <form onSubmit={handleLogin} className="research-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="password">Admin Password</label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={loginData.password}
                      onChange={handleLoginChange}
                      required
                      placeholder="Enter admin password"
                      autoFocus
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={() => setShowLoginForm(false)} className="cancel-btn">
                    Cancel
                  </button>
                  <button type="submit" className="save-btn">
                    Login
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      )}


      {/* Add/Edit Form Modal */}
      {showAddForm && isLoggedIn && (
        <section className="form-modal">
          <div className="container">
            <div className="form-modal-content">
              <div className="form-header">
                <h3>{editingItem ? '✏️ Edit Research Paper' : '➕ Add New Research Paper'}</h3>
                <button onClick={() => setShowAddForm(false)} className="close-btn">✕</button>
              </div>

              <form onSubmit={handleFormSubmit} className="research-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="title">Title *</label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleFormChange}
                      required
                      placeholder="Enter research paper title"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="author">Author(s) *</label>
                    <input
                      type="text"
                      id="author"
                      name="author"
                      value={formData.author}
                      onChange={handleFormChange}
                      required
                      placeholder="Enter author names"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="journal">Journal</label>
                    <input
                      type="text"
                      id="journal"
                      name="journal"
                      value={formData.journal}
                      onChange={handleFormChange}
                      placeholder="Enter journal name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="year">Year</label>
                    <input
                      type="text"
                      id="year"
                      name="year"
                      value={formData.year}
                      onChange={handleFormChange}
                      placeholder="2024"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="url">URL</label>
                    <input
                      type="url"
                      id="url"
                      name="url"
                      value={formData.url}
                      onChange={handleFormChange}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="studytype">Study Type</label>
                    <select
                      id="studytype"
                      name="studytype"
                      value={formData.studytype}
                      onChange={handleFormChange}
                    >
                      <option value="">Select study type</option>
                      <optgroup label="Empirical Studies">
                        <option value="Outcome">Outcome</option>
                        <option value="Process-outcome">Process-outcome</option>
                        <option value="Process">Process</option>
                        <option value="Correlational">Correlational</option>
                        <option value="Measurement validation">Measurement validation</option>
                      </optgroup>
                      <optgroup label="Reviews">
                        <option value="Review">Review</option>
                      </optgroup>
                      <optgroup label="Theoretical">
                        <option value="Theoretical">Theoretical</option>
                      </optgroup>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="methodology">Methodology</label>
                    <select
                      id="methodology"
                      name="methodology"
                      value={formData.methodology}
                      onChange={handleFormChange}
                    >
                      <option value="">Select methodology</option>
                      <optgroup label="Empirical Methods">
                        <option value="Quantitative">Quantitative</option>
                        <option value="Qualitative">Qualitative</option>
                        <option value="Mixed methods">Mixed methods</option>
                      </optgroup>
                      <optgroup label="Review Types">
                        <option value="Systematic">Systematic</option>
                        <option value="Scoping">Scoping</option>
                        <option value="Traditional">Traditional</option>
                        <option value="Overview">Overview</option>
                      </optgroup>
                      <optgroup label="Theoretical Types">
                        <option value="Practice description">Practice description</option>
                        <option value="Conceptual discussion">Conceptual discussion</option>
                        <option value="Intervention development">Intervention development</option>
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="researchdesign">Research Design</label>
                    <select
                      id="researchdesign"
                      name="researchdesign"
                      value={formData.researchdesign}
                      onChange={handleFormChange}
                    >
                      <option value="">Select research design</option>
                      <optgroup label="Quantitative Designs">
                        <option value="RCT">RCT</option>
                        <option value="Experiment">Experiment</option>
                        <option value="Quasi-experiment">Quasi-experiment</option>
                        <option value="Single-group">Single-group</option>
                        <option value="Case-study">Case-study</option>
                        <option value="Cross-sectional">Cross-sectional</option>
                        <option value="Longitudinal">Longitudinal</option>
                        <option value="Delphi">Delphi study</option>
                      </optgroup>
                      <optgroup label="Qualitative Designs">
                        <option value="Thematic analysis">Thematic analysis</option>
                        <option value="Phenomenological analysis">Phenomenological analysis</option>
                        <option value="Content analysis">Content analysis</option>
                        <option value="Discourse analysis">Discourse analysis</option>
                        <option value="Grounded theory">Grounded theory</option>
                        <option value="Case analysis">Case analysis</option>
                        <option value="Action research">Action research</option>
                        <option value="Generic">Generic</option>
                        <option value="Microanalysis">Microanalysis</option>
                        <option value="Narrative analysis">Narrative analysis</option>
                      </optgroup>
                      <optgroup label="Systematic Review Designs">
                        <option value="Meta-analysis">Meta-analysis</option>
                        <option value="Meta-synthesis">Meta-synthesis</option>
                        <option value="Descriptive">Descriptive</option>
                        <option value="Bibliometric">Bibliometric</option>
                      </optgroup>
                      <optgroup label="Other">
                        <option value="Mixed design">Mixed design</option>
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={() => setShowAddForm(false)} className="cancel-btn">
                    Cancel
                  </button>
                  <button type="submit" className="save-btn">
                    {editingItem ? 'Update Research' : 'Add Research'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* Introduction Section */}
      <section className="introduction-section">
        <div className="container">
          <div className="intro-content">
            <h2>About the SFA PubList</h2>
            <p>
              Considering the growing interest in the solution-focused approach marked by a continuous increase in published papers with limited access to those outside of academia, in the late 1990s Dr Alasdair Macdonald started the Solution-Focused Brief Therapy Evaluation List – an online collection of short summaries and references to empirical research relevant to the establishment and recognition of the approach.
            </p>
            <p>
              Starting from 2018 the Research Task Group of the European Brief Therapy Association (EBTA RTG) has the privilege and responsibility to continue the maintenance of the list. We actively seek to identify and collect references to published papers on the topic of solution-focused approach as it is applied, researched, or discussed in various settings, contexts, and countries with no language restrictions.
            </p>

            <div className="contact-info">
              <p>
                <strong>Note:</strong> The list is not exhaustive and includes only publications accessible to the EBTA RTG.
                If you have published an article or have come across one that is not on the SFA PubList by EBTA,
                please send the reference with a link to: <a href="mailto:research@ebta.eu">research@ebta.eu</a>
              </p>
              <p>
                <strong>Special thanks:</strong> to Marie-Carmen Neipp and Mark Beyebach for giving us permission to update the SFA PubList by EBTA by using their collection of papers included in their review paper: Neipp, M-C., Beyebach, M. (2022). The Global Outcomes of Solution-Focused Brief Therapy: A Revision. The American Journal of Family Therapy. <a href="https://doi.org/10.1080/01926187.2022.2069175" target="_blank" rel="noopener noreferrer">https://doi.org/10.1080/01926187.2022.2069175</a>
              </p>
              <p>
                <strong>Contributors:</strong> We thank Alasdair Macdonald, Dmitry Stebakov, and Evgeniya Kuminskaya for their past contributions.
                Current maintainers: Andreea Żak, Rytis Pakrosnis, Elena Lavrova.
              </p>
            </div>

            <div className="stats-overview">
              <h3>The List at a Glance</h3>
              <p><strong>A total of {stats.totalPapers || 0} papers, published between {stats.minYear} and {stats.maxYear};</strong></p>

              <div className="empirical-breakdown">
                <p><strong>{stats.empiricalStudies || 0} empirical studies</strong>, including {stats.outcome || 0} outcome, {stats.processOutcome || 0} process-outcome, {stats.process || 0} process, {stats.correlational || 0} correlational and {stats.measurementValidation || 0} measurement validation studies. {stats.quantitative || 0} studies used quantitative, {stats.qualitative || 0} – qualitative, and {stats.mixedMethods || 0} – mixed research methods:</p>
              </div>

              <div className="methodology-breakdown">
                <p><strong>Within the quantitative methodology</strong> ({stats.quantitativeDesignTotal || 0} studies) there were {stats.rct || 0} RCTs, {stats.experiments || 0} experiments, {stats.quasiExperiments || 0} quasi-experiments, {stats.singleGroup || 0} single-group studies, {stats.caseStudyQuant || 0} case-studies, {stats.crossSectional || 0} cross-sectional, {stats.longitudinal || 0} longitudinal and {stats.delphi || 0} Delphi studies;</p>

                <p><strong>Within the qualitative methodology</strong> ({stats.qualitativeDesignTotal || 0} studies) there were {stats.thematicAnalysis || 0} thematic analyses, {stats.caseAnalysis || 0} case analyses, {stats.contentAnalysis || 0} content analyses, {stats.microanalysis || 0} microanalyses, {stats.narrativeAnalysis || 0} narrative analyses, {stats.phenomenologicalAnalysis || 0} phenomenological analyses, {stats.discourseAnalysis || 0} discourse analyses, {stats.groundedTheory || 0} grounded theory analyses, {stats.actionResearch || 0} action researches, and {stats.genericQualitative || 0} generic studies.</p>
              </div>

              <div className="reviews-breakdown">
                <p><strong>{stats.reviews || 0} reviews</strong>, including {stats.systematicReviews || 0} systematic reviews (among them {stats.metaAnalyses || 0} meta-analyses, {stats.metaSyntheses || 0} meta-syntheses, {stats.descriptiveReviews || 0} descriptive and {stats.bibliometricReviews || 0} bibliometric), {stats.scopingReviews || 0} – Scoping, {stats.traditionalReviews || 0} – traditional and {stats.overviewReviews || 0} overviews.</p>
              </div>

              <div className="theoretical-breakdown">
                <p><strong>{stats.theoretical || 0} theoretical papers</strong>: {stats.practiceDescription || 0} presenting practice descriptions, {stats.conceptualDiscussion || 0} – conceptual discussions, and {stats.interventionDevelopment || 0} describing intervention development.</p>
              </div>
            </div>

            <div className="research-trends">
              <h4>Research Trends from the SFA PubList by EBTA</h4>
              <p>
                Starting in 2023, the EBTA Research Task Group conducts annual Scoping reviews of research concerned with the outcome and/or process of the solution-focused approach published in the previous year to collect emerging trends and give a picture of where we have come, where it is worth going with the research and where do we stand with the evidence base of the approach.
              </p>
              <div className="trend-links">
                <a href="https://journalsfp.org/article/90976-research-on-the-solution-focused-approach-in-2022-a-scoping-review" className="trend-link" target="_blank" rel="noopener noreferrer">
                  📊 Research Trends in 2022
                </a>
                <a href="https://journalsfp.org/article/127301-main-trends-in-research-on-the-solution-focused-approach-in-2023-a-scoping-review" className="trend-link" target="_blank" rel="noopener noreferrer">
                  📊 Research Trends in 2023
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="search-filters-section">
        <div className="container">
          <div className="search-filters">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search by keyword: title, author, journal, study type, methodology..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="filters">
              <select
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">All Years</option>
                {uniqueYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>

              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">All Study Types</option>
                {uniqueTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              <select
                value={methodologyFilter}
                onChange={(e) => setMethodologyFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">All Methodologies</option>
                {uniqueMethodologies.map(methodology => (
                  <option key={methodology} value={methodology}>{methodology}</option>
                ))}
              </select>

              <select
                value={researchDesignFilter}
                onChange={(e) => setResearchDesignFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">All Research Designs</option>
                {uniqueDesigns.map(design => (
                  <option key={design} value={design}>{design}</option>
                ))}
              </select>

              <button onClick={clearFilters} className="clear-filters-btn">
                Clear Filters
              </button>

              {isLoggedIn ? (
                <div className="admin-controls-subtle">
                  <button
                    onClick={handleAddNew}
                    className="admin-toggle-btn-subtle"
                    title="Add new research paper"
                  >
                    ➕
                  </button>
                  <button
                    onClick={handleDownloadExcel}
                    className="admin-toggle-btn-subtle"
                    title="Download as Excel"
                  >
                    📊
                  </button>
                  <button
                    onClick={handleLogout}
                    className="logout-btn-subtle"
                    title="Logout"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowLoginForm(true)}
                  className="login-btn-subtle"
                  title="Admin Login"
                >
                  🔐
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Research Results */}
      <section className="research-results-section">
        <div className="container">
          {filteredData.length === 0 ? (
            <div className="no-results">
              <p>No research found matching your criteria.</p>
              <button onClick={clearFilters} className="clear-filters-btn">
                Show All Research
              </button>
            </div>
          ) : (
            <>
              <div className="results-header">
                <p className="results-info">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredData.length)} of {filteredData.length} results
                </p>
              </div>

              <div className="research-grid">
                {currentItems.map((item, index) => (
                  <div
                    key={startIndex + index}
                    className="research-item"
                    onMouseMove={(e) => handleMouseMove(e, startIndex + index)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="research-header">
                      <h3 className="research-title">{item.title}</h3>
                      <div className="research-meta">
                        {item.year && (
                          <button
                            onClick={() => setYearFilter(item.year)}
                            className="research-year filter-button"
                            title={`Filter by year ${item.year}`}
                          >
                            {item.year}
                          </button>
                        )}
                        {item.studytype && (
                          <button
                            onClick={() => setTypeFilter(item.studytype)}
                            className="research-type filter-button"
                            title={`Filter by type ${item.studytype}`}
                          >
                            {item.studytype}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="research-details">
                      {item.author && (
                        <p className="research-authors">
                          <strong>Authors:</strong> {item.author}
                        </p>
                      )}

                      {item.journal && (
                        <p className="research-journal">
                          <strong>Journal:</strong> {item.journal}
                        </p>
                      )}

                      <div className="research-meta-info">
                        {item.methodology && (
                          <button
                            onClick={() => setSearchTerm(item.methodology)}
                            className="meta-tag methodology filter-button"
                            title={`Filter by methodology ${item.methodology}`}
                          >
                            {item.methodology}
                          </button>
                        )}
                        {item.researchdesign && (
                          <button
                            onClick={() => setSearchTerm(item.researchdesign)}
                            className="meta-tag design filter-button"
                            title={`Filter by research design ${item.researchdesign}`}
                          >
                            {item.researchdesign}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="research-actions">
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="research-link"
                        >
                          🔗 View Article
                        </a>
                      )}

                      {isLoggedIn && serverStatus && serverStatus.mongodb === 'connected' && (
                        <div className="admin-actions-item">
                          <button
                            onClick={() => handleEdit(item)}
                            className="edit-btn"
                            title="Edit this research paper"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDelete(item)}
                            className="delete-btn"
                            title="Delete this research paper"
                          >
                            🗑️
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    ← Previous
                  </button>

                  <div className="pagination-numbers">
                    {getPaginationNumbers().map((number, index) => (
                      <button
                        key={index}
                        onClick={() => typeof number === 'number' ? goToPage(number) : null}
                        className={`pagination-number ${currentPage === number ? 'active' : ''} ${typeof number !== 'number' ? 'dots' : ''}`}
                        disabled={typeof number !== 'number'}
                      >
                        {number}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Floating Tooltip */}
      {hoveredItem !== null && currentItems[hoveredItem - startIndex] && (
        <div
          className="floating-tooltip"
          style={{
            left: `${tooltipPos.x}px`,
            top: `${tooltipPos.y}px`
          }}
        >
          {getTooltipText(currentItems[hoveredItem - startIndex])}
        </div>
      )}
    </div>
  );
};

export default SFResearch;
